import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Workspaces as GroupIcon,
  Add as AddIcon,
  Settings as ManageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { LoadingScreen } from '../../components/common/LoadingScreen';

interface LocationGroup {
  id: number;
  name: string;
  type: string;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  member_count: number;
}

export default function LocationGroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<LocationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<LocationGroup | null>(null);

  // Create dialog state
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const systemGroups = groups.filter((g) => g.type === 'system');
  const customGroups = groups.filter((g) => g.type !== 'system');

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<LocationGroup[]>('/admin/location-groups');
      setGroups(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load location groups');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleOpenCreate = () => {
    setNewName('');
    setNewDescription('');
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCreate = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      await apiClient.post('/admin/location-groups', {
        name: newName,
        description: newDescription || null,
      });
      setCreateDialogOpen(false);
      await loadGroups();
    } catch (err: unknown) {
      setCreateError(extractErrorMessage(err, 'Failed to create location group'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteGroup = (group: LocationGroup) => {
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedGroup) return;

    try {
      await apiClient.delete(`/admin/location-groups/${selectedGroup.id}`);
      await loadGroups();
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to delete location group'));
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading location groups..." />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            Error loading location groups: {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <GroupIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Location Groups
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            size="small"
          >
            Create Group
          </Button>
        </Box>

        {/* System Groups */}
        {systemGroups.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>System Groups</Typography>
            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Locations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systemGroups.map((group) => (
                    <TableRow
                      key={group.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/location-groups/${group.id}`)}
                    >
                      <TableCell>{group.name}</TableCell>
                      <TableCell>{group.description || '-'}</TableCell>
                      <TableCell align="right">{group.member_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Custom Groups */}
        <Typography variant="h6" sx={{ mb: 1 }}>Custom Groups</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Locations</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customGroups.map((group) => (
                <TableRow
                  key={group.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/location-groups/${group.id}`)}
                >
                  <TableCell>{group.name}</TableCell>
                  <TableCell>{group.description || '-'}</TableCell>
                  <TableCell align="right">{group.member_count}</TableCell>
                  <TableCell>{formatDate(group.created_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/location-groups/${group.id}`); }}
                      title="Manage group"
                    >
                      <ManageIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group); }}
                      title="Delete group"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {customGroups.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No custom groups yet
            </Typography>
          </Box>
        )}

        {/* Create Group Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Location Group</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {createError && (
                <Alert severity="error" onClose={() => setCreateError(null)}>
                  {createError}
                </Alert>
              )}
              <TextField
                label="Group Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={createLoading || !newName.trim()}
            >
              {createLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Location Group"
          message={`Are you sure you want to delete "${selectedGroup?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmColor="error"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setSelectedGroup(null);
          }}
        />
      </Box>
    </Container>
  );
}
